import React, {
  createContext,
  PropsWithChildren,
  useState,
  useContext,
  useCallback,
  useRef,
} from 'react';
import { getShipmentsPaged } from '../../services/VoyageRepository';
import { useReportingFilters } from './ReportingFiltersContext';
import type { ApiRecord, RequestParams } from './types';

export interface ShipmentPageInfo {
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface ShipmentTotals {
  adjustedPoRequest: number;
  finalAgreement: number;
  effectiveShipped: number;
  actualInflow: number;
}

export interface FetchShipmentDetailsOptions {
  page?: number;
  size?: number;
  nearDate?: string;
  search?: string;
  columnFilters?: string;
  sortModel?: string;
}

export interface FetchShipmentDetailsResult {
  pageNumber: number;
  nearDateRowIndex?: number | null;
}

export interface ReportingShipmentDataContextValue {
  shipmentDetails: ApiRecord[];
  shipmentDetailsBase: ApiRecord[];
  shipmentPageInfo: ShipmentPageInfo;
  shipmentTotals: ShipmentTotals;
  shipmentNearDateRowIndex: number | null;
  fetchShipmentDetailsPage: (
    options?: FetchShipmentDetailsOptions
  ) => Promise<FetchShipmentDetailsResult>;
  isShipmentDetailsLoading: boolean;
}

const ReportingShipmentDataContext =
  createContext<ReportingShipmentDataContextValue | null>(null);

const toLocalDateOnly = (dateString: string | null | undefined) => {
  if (!dateString) return null;
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

export const ReportingShipmentDataProvider = ({
  children,
}: PropsWithChildren) => {
  const { buildParams, activeScenario, showError } = useReportingFilters();

  const [isShipmentDetailsLoading, setIsShipmentDetailsLoading] =
    useState(false);
  const shipmentDetailsRequestIdRef = useRef(0);
  const [shipmentDetails, setShipmentDetails] = useState<ApiRecord[]>([]);
  const [shipmentDetailsBase, setShipmentDetailsBase] = useState<ApiRecord[]>(
    []
  );
  const [shipmentPageInfo, setShipmentPageInfo] = useState<ShipmentPageInfo>({
    totalElements: 0,
    totalPages: 0,
    number: 0,
    size: 50,
  });
  const [shipmentTotals, setShipmentTotals] = useState<ShipmentTotals>({
    adjustedPoRequest: 0,
    finalAgreement: 0,
    effectiveShipped: 0,
    actualInflow: 0,
  });
  const [shipmentNearDateRowIndex, setShipmentNearDateRowIndex] = useState<
    number | null
  >(null);

  const fetchShipmentDetailsPage = useCallback(
    async ({
      page = 0,
      size = 50,
      nearDate,
      search,
      columnFilters,
      sortModel,
    }: FetchShipmentDetailsOptions = {}): Promise<FetchShipmentDetailsResult> => {
      // Only the newest request may write state or clear the loading flag;
      // responses of superseded requests are discarded.
      const requestId = shipmentDetailsRequestIdRef.current + 1;
      shipmentDetailsRequestIdRef.current = requestId;
      const isStale = () => shipmentDetailsRequestIdRef.current !== requestId;

      setIsShipmentDetailsLoading(true);
      try {
        const paramsObj = Object.fromEntries(buildParams().entries());
        const params: RequestParams = { ...paramsObj, page, size };
        if (nearDate) params.nearDate = nearDate;
        if (search) params.search = search;
        if (columnFilters) params.columnFilters = columnFilters;
        if (sortModel) params.sortModel = sortModel;

        const scenarioFetch = getShipmentsPaged(params);

        // When scenario is active, also fetch base (without scenarioId) for comparison
        let baseFetch: Promise<ApiRecord> | undefined;
        if (activeScenario) {
          const baseParams = { ...params };
          delete baseParams.scenarioId;
          baseFetch = getShipmentsPaged(baseParams).catch(() => ({
            content: [],
          }));
        }

        const response = await scenarioFetch;
        const {
          content,
          totalElements,
          totalPages,
          number: pageNumber,
          totals,
          nearDateRowIndex,
        } = response;

        if (isStale())
          return { pageNumber: pageNumber ?? page, nearDateRowIndex };

        const transformedData = (content || []).map((shipment: ApiRecord) => ({
          ...shipment,
          initialEtd: toLocalDateOnly(shipment.initialEtd),
          voyageLastUpdatedAt: toLocalDateOnly(shipment.voyageLastUpdatedAt),
          shipmentLastUpdatedAt: toLocalDateOnly(
            shipment.shipmentLastUpdatedAt
          ),
          actualEtd: toLocalDateOnly(shipment.actualEtd),
          initialEta: toLocalDateOnly(shipment.initialEta),
          actualEta: toLocalDateOnly(shipment.actualEta),
          shipLastUpdatedAt: toLocalDateOnly(shipment.shipLastUpdatedAt),
        }));

        setShipmentDetails(transformedData);
        setShipmentPageInfo({
          totalElements: totalElements || 0,
          totalPages: totalPages || 0,
          number: pageNumber ?? page,
          size,
        });
        if (totals) setShipmentTotals(totals);
        setShipmentNearDateRowIndex(nearDateRowIndex ?? null);

        if (baseFetch) {
          const baseResponse = await baseFetch;
          if (isStale())
            return { pageNumber: pageNumber ?? page, nearDateRowIndex };
          setShipmentDetailsBase(
            (baseResponse.content || []).map((shipment: ApiRecord) => ({
              ...shipment,
              initialEtd: toLocalDateOnly(shipment.initialEtd),
              voyageLastUpdatedAt: toLocalDateOnly(
                shipment.voyageLastUpdatedAt
              ),
              shipmentLastUpdatedAt: toLocalDateOnly(
                shipment.shipmentLastUpdatedAt
              ),
              actualEtd: toLocalDateOnly(shipment.actualEtd),
              initialEta: toLocalDateOnly(shipment.initialEta),
              actualEta: toLocalDateOnly(shipment.actualEta),
              shipLastUpdatedAt: toLocalDateOnly(shipment.shipLastUpdatedAt),
            }))
          );
        } else {
          setShipmentDetailsBase([]);
        }

        return { pageNumber: pageNumber ?? page, nearDateRowIndex };
      } catch (error) {
        if (isStale()) return { pageNumber: page };
        showError(error, 'fetchShipmentDetailsPage');
        setShipmentDetails([]);
        setShipmentDetailsBase([]);
        setShipmentPageInfo((prev) => ({
          ...prev,
          totalElements: 0,
          totalPages: 0,
        }));
        return { pageNumber: 0 };
      } finally {
        if (!isStale()) setIsShipmentDetailsLoading(false);
      }
    },
    [buildParams, activeScenario, showError]
  );

  return (
    <ReportingShipmentDataContext.Provider
      value={{
        shipmentDetails,
        shipmentDetailsBase,
        shipmentPageInfo,
        shipmentTotals,
        shipmentNearDateRowIndex,
        fetchShipmentDetailsPage,
        isShipmentDetailsLoading,
      }}
    >
      {children}
    </ReportingShipmentDataContext.Provider>
  );
};

export const useReportingShipmentData =
  (): ReportingShipmentDataContextValue => {
    const ctx = useContext(ReportingShipmentDataContext);
    if (ctx === null) {
      throw new Error(
        'useReportingShipmentData must be used within a <ReportingShipmentDataProvider>'
      );
    }
    return ctx;
  };
